'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import { useAppSelector } from '@/utils/redux';
import styles from './LocationMap.module.scss';
import { MapProps } from './LocationMapWrapper';

const mapLoadingState = (
  <div className={styles.mapContainer}>
    <ShimmerContainer className={styles.shimmerContainer}>
      <div />
    </ShimmerContainer>
  </div>
);

const LocationMapWrapper = dynamic(() => import('./LocationMapWrapper'), {
  ssr: false,
  loading: () => mapLoadingState
});

const DynamicLocationMap = (props: MapProps) => {
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useIsVisible(mapWrapperRef, { rootMargin: '100px' });
  const [isDeferredMapLoaded, setIsDeferredMapLoaded] = useState(false);
  const isMobileBrowser = useAppSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);
  const isTabletOrMobile = useMediaQuery(`(max-width: 1120px)`, isMobileBrowser);

  // Defer loading the map until it's in view on mobile
  // This has performance benefits to page load time and INP, that needs to be taken into account
  // if we decide to move the map higher up the page on mobile at a later time.
  useEffect(() => {
    if (isInView && isTabletOrMobile) {
      setIsDeferredMapLoaded(true);
    }
  }, [isInView]);

  const showMap = !isTabletOrMobile || isDeferredMapLoaded;

  if (!showMap) {
    return <div ref={mapWrapperRef}>{mapLoadingState}</div>;
  }

  return <LocationMapWrapper {...props} />;
};

export default DynamicLocationMap;
